type ImageStatus = 'pending' | 'loading' | 'loaded' | 'error';

class PreloadImagesTool {
	// Cache for loaded images and their statuses
	private cache: Map<string, Promise<void>> = new Map();
	private status: Map<string, ImageStatus> = new Map();

	// Queue for background preloading
	private queue: string[] = [];
	private running = 0;
	private maxConcurrency = 4;

	/**
	 * Preload an array of image URLs in the background with a given concurrency.
	 * @param urls Array of image URLs to preload.
	 * @param concurrency Number of parallel loads (default: 4).
	 */
	preloadImages(urls: string[], concurrency: number = 4) {
		this.maxConcurrency = concurrency;
		for (const url of urls) {
			if (!this.cache.has(url)) {
				this.queue.push(url);
				this.status.set(url, 'pending');
			}
		}
		this.processQueue();
	}

	/**
	 * Force-load an image and return a promise that resolves when loaded.
	 * If already loaded or loading, returns the existing promise.
	 * @param url Image URL to load.
	 */
	loadImageDirectly(url: string): Promise<void> {
		if (this.cache.has(url)) {
			return this.cache.get(url)!;
		}
		const promise = this.loadImage(url);
		this.cache.set(url, promise);
		this.status.set(url, 'loading');
		return promise;
	}

	private processQueue() {
		while (this.running < this.maxConcurrency && this.queue.length > 0) {
			const url = this.queue.shift()!;
			if (this.status.get(url) === 'pending') {
				this.running++;
				this.status.set(url, 'loading');
				const promise = this.loadImage(url)
					.catch(() => { /* ignore errors */ })
					.finally(() => {
						this.running--;
						this.processQueue();
					});
				this.cache.set(url, promise);
			}
		}
	}

	private loadImage(url: string): Promise<void> {
		return new Promise((resolve, reject) => {
			const img = new window.Image();
			img.onload = () => {
				this.status.set(url, 'loaded');
				console.log('LOADED: ', url)
				resolve();
			};
			img.onerror = () => {
				this.status.set(url, 'error');
				reject(new Error(`Failed to load image: ${url}`));
			};
			img.src = url;
		});
	}

	/**
	 * Check if an image is already loaded.
	 */
	isLoaded(url: string): boolean {
		return this.status.get(url) === 'loaded';
	}
}

const preloadImagesTool = new PreloadImagesTool();
export default preloadImagesTool;
